import { Alert, AlertDescription, AlertIcon } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { parseCheckIpError } from "hooks/api/checkIpError";

/**
 * Explains a failed `/check-ip` lookup instead of a generic error or an
 * endless spinner. A rate limit is a warning (it clears by itself, and the
 * message says when); a provider outage or an unknown failure is an error.
 */
const CheckIpErrorMessage = ({ error }: { error: unknown }) => {
  const intl = useIntl();
  const parsed = parseCheckIpError(error);

  let text: string;
  if (parsed.reason === "rate_limited") {
    text = parsed.retryAfter
      ? intl.formatMessage(
          { id: "checkIp.error.rateLimitedSeconds" },
          { seconds: parsed.retryAfter }
        )
      : intl.formatMessage({ id: "checkIp.error.rateLimited" });
  } else {
    text = intl.formatMessage({ id: `checkIp.error.${parsed.reason}` });
  }
  const isWarning = parsed.reason === "rate_limited";
  const accent = isWarning ? "status.warning" : "status.error";

  return (
    <Alert
      status={isWarning ? "warning" : "error"}
      variant="left-accent"
      borderRadius="md"
      bg={isWarning ? "status.warningSubtle" : "status.errorSubtle"}
      color="text.primary"
      borderColor={accent}
      my={4}
      data-testid="check-ip-error"
      data-reason={parsed.reason}
    >
      <AlertIcon color={accent} />
      <AlertDescription fontSize="sm">{text}</AlertDescription>
    </Alert>
  );
};

export default CheckIpErrorMessage;
